import React, { useState, useRef, useEffect, useId } from 'react';
import { ChevronDownIcon, PlusIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import Modal from '../Modal';
import useSearchLookup from './useSearchLookup';
import useIsMobile from '../../../hooks/useIsMobile';

const ResultList = ({
  isLoading,
  results,
  query,
  focusedIndex,
  createNewIndex,
  themedBg,
  icon,
  onItemSelect,
  onCreateNew,
  createNewLabel,
  resultCap,
  showCapHint,
}) => {

  if (isLoading) {
    return (
      <div className={`relative cursor-default select-none py-2 px-3 ${themedBg ? themedBg.textPrimary : 'text-ink-900'}`}>
        <div className="flex items-center">
          <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${themedBg?.isDark ? 'border-ink-200' : 'border-ink-900'}`}></div>
          <span className={`ml-2 ${themedBg ? themedBg.textSecondary : 'text-ink-500'}`}>Searching...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {results.length > 0 ? (
        results.map((item, index) => (
          <div
            key={item.id}
            className={`relative cursor-pointer select-none py-2 px-3 transition-colors duration-150 ${
              index === focusedIndex
                ? `text-white ${!themedBg ? 'bg-accent-500' : ''}`
                : `${themedBg ? themedBg.textPrimary : 'text-ink-900'} ${themedBg?.isDark ? 'hover:bg-ink-600' : 'hover:bg-cream-200'}`
            }`}
            style={index === focusedIndex && themedBg ? { backgroundColor: 'var(--theme-primary)' } : {}}
            onClick={() => onItemSelect(item)}
            role="option"
            aria-selected={index === focusedIndex}
          >
            <div className="flex items-center w-full min-w-0">
              {icon && (
                <div className="flex-shrink-0 w-5 h-5 mr-3">
                  <div className={`h-5 w-5 ${
                    index === focusedIndex ? 'text-white opacity-75' : 'text-ink-400'
                  }`}>
                    {icon}
                  </div>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="break-words font-medium" title={item.name}>
                  {item.name}
                </div>
                {item.secondary && (
                  <div
                    className={`break-words text-sm ${
                      index === focusedIndex ? 'text-white/80' : (themedBg ? themedBg.textSecondary : 'text-ink-500')
                    }`}
                    title={item.secondary}
                  >
                    {item.secondary}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      ) : query ? (
        <div className={`relative cursor-default select-none py-2 px-3 ${themedBg ? themedBg.textPrimary : 'text-ink-900'}`}>
          <span className={themedBg ? themedBg.textSecondary : 'text-ink-500'}>No results found</span>
        </div>
      ) : null}

      {showCapHint && resultCap && results.length >= resultCap && (
        <div className={`px-3 py-2 text-xs italic ${themedBg ? themedBg.textSecondary : 'text-ink-500'}`}>
          Showing first {resultCap} — type to search for more
        </div>
      )}

      {onCreateNew && (
        <>
          {results.length > 0 && (
            <div className={`mx-2 border-t ${themedBg ? themedBg.borderClass : 'border-ink-100'}`} />
          )}
          <div
            className={`relative cursor-pointer select-none py-2 px-3 transition-colors duration-150 ${
              focusedIndex === createNewIndex
                ? `text-white ${!themedBg ? 'bg-accent-500' : ''}`
                : `${themedBg ? '' : 'text-accent-600'} ${themedBg ? (themedBg.isDark ? 'hover:bg-ink-600' : 'hover:bg-cream-100') : 'hover:bg-cream-100'}`
            }`}
            style={
              focusedIndex === createNewIndex && themedBg
                ? { backgroundColor: 'var(--theme-primary)' }
                : (themedBg && focusedIndex !== createNewIndex ? { color: 'var(--theme-primary)' } : {})
            }
            onClick={() => onCreateNew()}
            role="option"
            aria-selected={focusedIndex === createNewIndex}
          >
            <div className="flex items-center">
              <PlusIcon
                className={`h-5 w-5 mr-2 ${
                  focusedIndex === createNewIndex
                    ? 'text-white'
                    : (themedBg ? '' : 'text-accent-600')
                }`}
                style={
                  themedBg && focusedIndex !== createNewIndex
                    ? { color: 'var(--theme-primary)' }
                    : {}
                }
              />
              <span className="font-medium">{createNewLabel}</span>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const SearchLookup = ({
  label,
  placeholder = 'Search...',
  icon,
  searchFunction,
  onSelect,
  isError = false,
  maxDisplayLength = 50,
  debounceMs = 300,
  disabled = false,
  required = false,
  optional = false,
  defaultValue = null,
  showChevron = false,
  allowEmptyQuery = false,
  clearOnSelect = false,
  themedBg = null,
  onCreateNew = null,
  createNewLabel = 'Add new',
  sheetTitle,
  sheetSearchPlaceholder,
  resultCap = null,
}) => {
  const isMobile = useIsMobile();
  const reactId = useId();
  const inputId = label ? `search-lookup-${label}-${reactId}` : `search-lookup-${reactId}`;
  const listboxId = `${inputId}-dropdown`;
  const [isOpen, setIsOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(
    defaultValue?.name
      ? { id: defaultValue.id ?? null, name: defaultValue.name }
      : null
  );
  const [displayValue, setDisplayValue] = useState(defaultValue?.name || '');
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Keep selection in sync when parent's defaultValue arrives or changes
  // (e.g. edit-mode pre-population via async React Query response).
  // Skip when the parent hands in an empty name — that's just a re-render
  // with the same "I have nothing for you" payload and must not clobber a
  // selection the user just made locally.
  //
  // Contract note: passing defaultValue={null} or {name: ''} AFTER mount is
  // therefore NOT a supported way to programmatically clear the picker.
  // To reset, remount the component via a changing `key=…` prop, or use
  // `clearOnSelect` for per-selection clears. (Users can clear interactively
  // via the inline × button rendered when displayValue is non-empty.)
  //
  // The effect is keyed on defaultValue's *value* (normalized id + name), not
  // its object identity. Parents commonly pass an inline object literal that
  // gets a new identity on every render — keying on identity would re-fire
  // this effect on every parent re-render and clobber an in-progress
  // edit/clear with the old value, even though nothing actually changed.
  //
  // Most parents pre-populate with a bare {name} and no id at all; one hands in
  // an explicit {id: null}. Coercing a missing id to null keeps those two shapes
  // from reading as distinct dep values.
  //
  // selectedItem is rebuilt from the keyed values rather than storing the
  // incoming object: it is only ever read for its `name` (handleInputChange),
  // and this effect never calls onSelect — consumers receive the full dropdown
  // item from handleItemSelect instead. Rebuilding keeps selectedItem a pure
  // function of the deps, so no stale object identity can be captured.
  const defaultId = defaultValue?.id ?? null;
  const defaultName = defaultValue?.name;

  useEffect(() => {
    if (!defaultName) return;
    setSelectedItem({ id: defaultId, name: defaultName });
    setDisplayValue(defaultName);
  }, [defaultId, defaultName]);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  const {
    query,
    setQuery,
    results,
    isLoading,
  } = useSearchLookup(searchFunction, debounceMs, allowEmptyQuery);

  // Close inline dropdown when clicking outside (desktop only)
  useEffect(() => {
    if (isMobile) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  useEffect(() => {
    setFocusedIndex(-1);
  }, [results]);

  const openSheet = () => {
    if (disabled) return;
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    setFocusedIndex(-1);
  };

  const handleTriggerClick = () => {
    if (isMobile) {
      openSheet();
    }
  };

  const handleInputChange = (e) => {
    if (isMobile) return; // mobile uses sheet's search input
    const value = e.target.value;

    if (value === getTruncatedDisplayValue() && displayValue.length > maxDisplayLength) {
      return;
    }

    setDisplayValue(value);
    setQuery(value);

    if (value && !isOpen) {
      setIsOpen(true);
    }

    if (selectedItem && value !== selectedItem.name) {
      setSelectedItem(null);
      onSelect?.(null);
    }
  };

  const handleInputFocus = () => {
    // Mobile opens via onClick / keyboard, not focus — the Dialog restores
    // focus to this input on close, and reopening on that focus would
    // create an inescapable reopen loop.
    if (isMobile) return;
    if (showChevron || query || results.length > 0) {
      setIsOpen(true);
      if (showChevron && !query) {
        setQuery('');
      }
    }
  };

  const handleChevronClick = () => {
    if (isMobile) {
      openSheet();
      return;
    }
    if (!isOpen) {
      setIsOpen(true);
      setQuery('');
      inputRef.current?.focus();
    } else {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);

    if (clearOnSelect) {
      setDisplayValue('');
      setQuery('');
    } else {
      setDisplayValue(item.name);
      setQuery('');
    }

    setIsOpen(false);
    setIsSheetOpen(false);
    setFocusedIndex(-1);
    onSelect?.(item);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    if (disabled) return;
    setSelectedItem(null);
    setDisplayValue('');
    setQuery('');
    setFocusedIndex(-1);
    setIsOpen(false);
    setIsSheetOpen(false);
    onSelect?.(null);
    if (!isMobile) {
      inputRef.current?.focus();
    }
  };

  const handleSheetQueryClear = () => {
    setQuery('');
    setFocusedIndex(-1);
  };

  const handleCreateNew = () => {
    if (!onCreateNew) return;
    onCreateNew(query);
    setIsOpen(false);
    setIsSheetOpen(false);
    setFocusedIndex(-1);
  };

  const totalItems = results.length + (onCreateNew ? 1 : 0);
  const createNewIndex = onCreateNew ? results.length : -1;

  const handleKeyDown = (e) => {
    if (isMobile) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        openSheet();
      }
      return;
    }

    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex === createNewIndex && onCreateNew) {
          handleCreateNew();
        } else if (focusedIndex >= 0 && results[focusedIndex]) {
          handleItemSelect(results[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  };

  const getTruncatedDisplayValue = () => truncateText(displayValue, maxDisplayLength);

  const inputClasses = [
    'w-full',
    'rounded-md',
    'border-0',
    'py-1.5',
    themedBg ? themedBg.textPrimary : 'text-ink-900',
    themedBg ? themedBg.cardBackground : 'bg-white',
    'shadow-sm',
    'ring-1',
    'ring-inset',
    themedBg ? '' : 'focus:ring-2 focus:ring-inset',
    themedBg ? 'focus-themed' : '',
    'text-base',
    'sm:text-sm',
    'sm:leading-6',
    'placeholder:text-ink-400',
    isError
      ? 'ring-signal-red-100 focus:ring-signal-red-500'
      : themedBg
        ? 'ring-themed-border hover-ring-themed-accent'
        : 'ring-ink-200 focus:ring-accent-500',
    icon ? 'pl-10' : 'pl-3',
    'pr-10',
    isMobile && !disabled ? 'cursor-pointer' : '',
  ].filter(Boolean).join(' ');

  // On mobile, suppress the native keyboard on the trigger input.
  // The sheet's own search input is the real text-entry field.
  const triggerKeyboardProps = isMobile
    ? { readOnly: true, inputMode: 'none' }
    : {};

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label
          htmlFor={inputId}
          className={`block text-sm font-medium leading-6 ${themedBg ? themedBg.textPrimary : 'text-ink-900'}`}
        >
          {label}
          {required && <span className="text-signal-red-500 ml-1">*</span>}
          {optional && (
            <span className="ml-1 font-normal text-ink-400">(optional)</span>
          )}
        </label>
      )}

      <div className={`relative ${label ? 'mt-2' : ''}`}>
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <div className="h-5 w-5 text-ink-400">
              {icon}
            </div>
          </div>
        )}

        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className={`${inputClasses} truncate`}
          placeholder={placeholder}
          value={getTruncatedDisplayValue()}
          title={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleTriggerClick}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={isMobile ? isSheetOpen : isOpen}
          aria-haspopup={isMobile ? 'dialog' : 'listbox'}
          aria-autocomplete={isMobile ? 'none' : 'list'}
          aria-controls={isMobile ? undefined : listboxId}
          aria-describedby={isError ? `${inputId}-error` : undefined}
          {...triggerKeyboardProps}
        />

        {displayValue && !disabled ? (
          <button
            type="button"
            className={`absolute inset-y-0 right-0 flex items-center pr-3 transition-opacity hover:opacity-70 ${themedBg ? themedBg.textSecondary : 'text-ink-400'} focus:outline-none focus-visible:opacity-70`}
            onClick={handleClear}
            aria-label="Clear address"
            title="Clear address"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : showChevron && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 hover:text-ink-600"
            onClick={handleChevronClick}
            disabled={disabled}
            aria-label="Show all options"
          >
            <ChevronDownIcon
              className={`h-5 w-5 text-ink-400 transition-transform duration-200 ${
                (isMobile ? isSheetOpen : isOpen) ? 'transform rotate-180' : ''
              }`}
            />
          </button>
        )}

        {/* Desktop inline dropdown */}
        {!isMobile && isOpen && (query || results.length > 0 || onCreateNew) && (
          <div
            ref={dropdownRef}
            id={listboxId}
            className={`absolute z-50 mt-1 max-h-60 sm:max-h-[22rem] w-full overflow-auto rounded-md ${themedBg ? themedBg.cardBackground : 'bg-white'} py-1 shadow-lg ring-1 ${themedBg ? themedBg.border : 'ring-black/5'}`}
            role="listbox"
          >
            <ResultList
              isLoading={isLoading}
              results={results}
              query={query}
              focusedIndex={focusedIndex}
              createNewIndex={createNewIndex}
              themedBg={themedBg}
              icon={icon}
              onItemSelect={handleItemSelect}
              onCreateNew={onCreateNew ? handleCreateNew : null}
              createNewLabel={createNewLabel}
              resultCap={resultCap}
              showCapHint={!query}
            />
          </div>
        )}
      </div>

      {/* Mobile bottom-sheet picker */}
      {isMobile && (
        <Modal
          open={isSheetOpen}
          onClose={closeSheet}
          title={sheetTitle || label || 'Select'}
          size="md"
          themedBg={themedBg}
          themeStyle={themedBg?.style}
        >
          <div>
            <div className="mb-4">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon className="h-5 w-5 text-ink-400" />
                </div>
                <input
                  type="text"
                  inputMode="search"
                  autoComplete="off"
                  placeholder={sheetSearchPlaceholder || placeholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className={`w-full rounded-md border-0 py-2 pl-10 pr-10 shadow-sm ring-1 ring-inset text-base sm:text-sm placeholder:text-ink-400 ${
                    themedBg
                      ? `${themedBg.textPrimary} ${themedBg.cardBackground} ring-themed-border focus-themed`
                      : 'text-ink-900 bg-white ring-ink-200 focus:ring-2 focus:ring-inset focus:ring-accent-500'
                  }`}
                  aria-label={sheetSearchPlaceholder || placeholder}
                />
                {query && (
                  <button
                    type="button"
                    className={`absolute inset-y-0 right-0 flex items-center pr-3 transition-opacity hover:opacity-70 ${themedBg ? themedBg.textSecondary : 'text-ink-400'} focus:outline-none focus-visible:opacity-70`}
                    onClick={handleSheetQueryClear}
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
            <div role="listbox" className="py-1">
              <ResultList
                isLoading={isLoading}
                results={results}
                query={query}
                focusedIndex={-1}
                createNewIndex={createNewIndex}
                themedBg={themedBg}
                icon={icon}
                onItemSelect={handleItemSelect}
                onCreateNew={onCreateNew ? handleCreateNew : null}
                createNewLabel={createNewLabel}
                resultCap={resultCap}
                showCapHint={!query}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SearchLookup;
